import { useState } from 'react';
import type { Section } from '../../shared/audience';
import { avatarUrl } from '../../shared/dto/offline';
import { useT } from '../i18n';
import { cx } from './ui';

/**
 * Section colours for FILLS only: backgrounds under white text, borders, bars, dots. Never as a
 * text colour — on the dark surfaces they fall below 4.5:1. Put a section label in --ink and let a
 * border or dot carry the colour (tests/unit/text-colour-usage.test.ts checks it).
 */
export const SECTION_COLOR: Record<Section, string> = {
  T1: 'var(--color-t1)',
  T2: 'var(--color-t2)',
  B1: 'var(--color-b1)',
  B2: 'var(--color-b2)',
  conductor: 'var(--color-conductor)',
  musician: 'var(--color-musician)',
};

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]![0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]![0] ?? '') : '';
  return (first + last).toUpperCase();
}

export function Avatar({
  userId,
  name,
  rev,
  section,
  size = 44,
  className,
}: {
  userId: string;
  name: string;
  rev: number;
  section: Section | null;
  size?: number;
  className?: string;
}) {
  const px = `${size}px`;
  const src = rev > 0 ? avatarUrl(userId, rev, size > 128 ? 512 : 128) : null;
  // A revision another device replaced or removed is 404 (rev URLs are never reused): show the initials
  // instead of an empty circle. Keyed by URL, so a new revision is tried again.
  const [failed, setFailed] = useState<string | null>(null);
  if (src && failed !== src) {
    return (
      <img
        src={src}
        onError={() => setFailed(src)}
        alt=""
        width={size}
        height={size}
        loading="lazy"
        decoding="async"
        className={cx('shrink-0 rounded-full bg-[var(--surface-2)] object-cover', className)}
        style={{ width: px, height: px }}
      />
    );
  }
  return (
    <span
      aria-hidden
      className={cx('inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white', className)}
      style={{ width: px, height: px, fontSize: size * 0.38, background: section ? SECTION_COLOR[section] : 'var(--color-musician)' }}
    >
      {initials(name)}
    </span>
  );
}

export function SectionBadge({ section, title, short }: { section: Section | null; title?: string | null; short?: boolean }) {
  const t = useT();
  if (!section) {
    return title ? <span className="rounded-full bg-[var(--surface-2)] px-2 py-0.5 text-[13px] font-semibold text-[var(--muted)]">{title}</span> : null;
  }
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[13px] font-semibold text-white"
      style={{ background: SECTION_COLOR[section] }}
    >
      {short ? t(`section.short.${section}`) : t(`section.${section}`)}
    </span>
  );
}
